import { IconButton } from "@material-ui/core";
import useDashboardEditor from "MyDashboards/state/editor";
import { EditorMode, Element } from "MyDashboards/state/editorTypes";
import { DashboardOpenMode } from "MyDashboards/state/types";
import px from "MyDashboards/utils/px";
import HighChart from "MyDashboards/views/ChartsTesting/HighChart";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { If, Then } from "react-if";
import { useWindowSize } from "react-use";
import { ReactComponent as DeleteIcon } from "../../assets/deleteIcon.svg";
import { ReactComponent as EditIcon } from "../../assets/edit_icon.svg";
import { TDashboardComponentRegistry } from "../ComponentRegistry/types";
import EditorPopup from "../EditorPopup";
import RenderText from "../Render/components/RichText/RenderText";
import useSlidesMenuWidth from "../SlidesMenu/useSlidesMenuWidth";
import AntSwitch from "./AntSwitch";
import InstructionsSlide from "./InstructionsSlide";
import RenderSlide from "./RenderSlide";
import EditSlide from "./SlideEditor";
import useSlidesViewStyles from "./themes";

import RedoIcon from "@material-ui/icons/Redo";
import UndoIcon from "@material-ui/icons/Undo";
import { useDashboard } from "MyDashboards/api/useDashboards.hook";
import useCurrentUser from "qpr-base/context/UserContext";
import * as renderers from "../Render";
interface SlideViewProps {
  selectedSlide?: any;
  openMode: DashboardOpenMode; // is affected by what page is opened viewdashboard/editdashboard
  registry?: TDashboardComponentRegistry;
}

const RenderElement = (props: { el: Element }) => {
  const ctx = useAppContext();
  const activePage = useDashboardEditor((s) =>
    s.pages.find((page) => page.id === s.selectedPageId)
  );
  if (props.el.component) {
    if (props.el.component.component === "Highchart") {
      return (
        <div
          key={`chart_${props.el.id}`}
          style={{
            width: px(props.el.component.width),
            height: px(props.el.component.height),
            transform: `scale(${props.el.width / props.el.component.width})`,
            transformOrigin: ctx.rtl ? "top right" : "top left",
          }}
        >
          <HighChart
            options={{
              ...props.el.component.data,
              width: props.el.component.width,
              height:
                props.el.component.width * (props.el.height / props.el.width),
            }}
          />
        </div>
      );
    }
    if (props.el.component.component === "Highcharts") {
      return <div>Highcharts component {props.el.component.data.message}</div>;
    }
  }

  if (props.el.rendered?.type === "text" && activePage) {
    return <RenderText el={props.el} slideNumber={activePage.slideNumber} />;
  }

  return <div />;
};

const SlideView: FunctionComponent<SlideViewProps> = (props) => {
  const classes = useSlidesViewStyles();
  const pages = useDashboardEditor((state) => state.pages);
  const page = useDashboardEditor((state) =>
    state.pages.find((p) => p.id === state.selectedPageId)
  );
  const editedElement = useDashboardEditor((state) => state.editedElement);
  const editorWidth = useDashboardEditor((state) => state.editorWidth);
  const { width, height } = useWindowSize();
  const setEditorMode = useDashboardEditor((e) => e.setEditorMode);
  const editorMode = useDashboardEditor((e) => e.mode); // is affected by edit/preivew toggle switch
  const removeSelectedElement = useDashboardEditor(
    (e) => e.removeSelectedElement
  );
  const { t } = useTranslation();
  const selectedElementId = useDashboardEditor((e) => e.selectedElementId);
  const selectedElements = useDashboardEditor((e) => e.selectedElements);
  const setEditedElement = useDashboardEditor((s) => s.setEditAction);
  const undo = useDashboardEditor((s) => s.undo);
  const redo = useDashboardEditor((s) => s.redo);
  const ctx = useAppContext();
  const { data: slide, isLoading: isDashboardLoading } = useDashboard(
    ctx.elementId
  );

  const areaReservedForLeft = useSlidesMenuWidth();
  const registry = props.registry || renderers.fullScreen;
  const isEditMode = editorMode === EditorMode.edit; //  && props.openMode !== DashboardOpenMode.view;

  React.useEffect(() => {
    if (
      props.openMode === DashboardOpenMode.view &&
      editorMode !== EditorMode.preview
    ) {
      setEditorMode(EditorMode.preview);
    }
    if (
      props.openMode === DashboardOpenMode.edit &&
      editorMode !== EditorMode.edit
    ) {
      setEditorMode(EditorMode.edit);
    }
  }, [props.openMode]);
  /**
   * Scale the editing area based on the size of the screen
   */
  const scaleFactor = React.useMemo(() => {
    const contentWidth = width - areaReservedForLeft;
    const contentHeight = height - 250;
    const editorHeight = (editorWidth * 9) / 16;
    if (contentWidth > editorWidth)
      return Math.min(contentHeight / editorHeight, contentWidth / editorWidth);
    if (contentWidth <= 100) return 0.1;
    return contentWidth / editorWidth;
  }, [width, height, page, editorWidth, areaReservedForLeft]);
  const activeUser = useCurrentUser();
  const isOwner = React.useMemo(() => {
    return slide?.creatorID === activeUser.profile?.meaUserId;
  }, [slide]);

  if (isDashboardLoading) {
    return null;
  }
  if (pages.length === 0 || !page) {
    return <InstructionsSlide />;
  }
  return (
    <div className={classes.container}>
      <div className={classes.toolbar}>
        <div className="left">
          <If condition={editorMode === EditorMode.edit}>
            <Then>
              <div className="toolIcon active">
                <IconButton
                  disabled={
                    selectedElementId === undefined &&
                    selectedElements.length !== 1
                  }
                  onClick={() => setEditedElement({ action: "edit" })}
                >
                  <EditIcon
                    style={{
                      fill: "currentcolor",
                      color:
                        selectedElementId === undefined &&
                        selectedElements.length !== 1
                          ? ""
                          : "rgb(0,100,216)",
                    }}
                  />
                </IconButton>
              </div>
              <div className="toolIcon">
                <IconButton
                  disabled={
                    selectedElementId === undefined &&
                    selectedElements.length === 0
                  }
                  onClick={() => {
                    removeSelectedElement();
                  }}
                >
                  <DeleteIcon
                    style={{
                      fill: "currentcolor",
                      color:
                        selectedElementId === undefined &&
                        selectedElements.length === 0
                          ? ""
                          : "#4C5157",
                    }}
                  />
                </IconButton>
              </div>
              <div
                className="toolIcon"
                style={{ transform: ctx.rtl ? "scaleX(-1)" : "" }}
              >
                <IconButton
                  onClick={() => {
                    undo();
                  }}
                >
                  <UndoIcon
                    style={{
                      fill: "currentcolor",
                      color:
                        selectedElementId === undefined &&
                        selectedElements.length === 0
                          ? ""
                          : "#4C5157",
                    }}
                  />
                </IconButton>
              </div>
              <div
                className="toolIcon"
                style={{ transform: ctx.rtl ? "scaleX(-1)" : "" }}
              >
                <IconButton
                  onClick={() => {
                    redo();
                  }}
                >
                  <RedoIcon
                    style={{
                      fill: "currentcolor",
                      color:
                        selectedElementId === undefined &&
                        selectedElements.length === 0
                          ? ""
                          : "#4C5157",
                    }}
                  />
                </IconButton>
              </div>
            </Then>
          </If>
        </div>
        <div className="switch">
          <span
            className={`label ${
              editorMode === EditorMode.edit ? "" : "inactive"
            }`}
          >
            {t("myStories.Edit")}
          </span>
          <AntSwitch
            disabled={!isOwner}
            checked={editorMode === EditorMode.edit}
            onChange={() => {
              if (isOwner) {
                if (editorMode === EditorMode.edit)
                  setEditorMode(EditorMode.preview);
                else if (editorMode === EditorMode.preview)
                  setEditorMode(EditorMode.edit);
              }
            }}
            name="checkedC"
          />
          <span
            className={`label ${
              editorMode !== EditorMode.edit ? "" : "inactive"
            }`}
          >
            {t("myStories.Preview")}
          </span>
        </div>
      </div>
      <div className={classes.slide}>
        <div
          style={{
            width: `${scaleFactor * editorWidth}px`,
            transform: `scale(${scaleFactor})`,
            transformOrigin: ctx.rtl ? "right top" : "left top",
          }}
        >
          <RenderSlide RenderElement={RenderElement} registry={registry} />
          <If condition={isEditMode}>
            <Then>
              <EditSlide scale={scaleFactor} />
            </Then>
          </If>
        </div>
        <If condition={isEditMode && editedElement !== undefined}>
          <Then>
            <EditorPopup />
          </Then>
        </If>
      </div>
    </div>
  );
};

export default SlideView;
